import { useEffect } from "react";

type Report = {
  screen: "page" | "layout";
  message: string;
  path: string;
  online: boolean;
  shownMs?: number;
};

const MAX_PENDING = 20;
const RETRY_MS = 10_000;
const pending: Report[] = [];
let flushing = false;

/** Plain fetch, not `api`: the report must not join the retries of the request that just failed. */
async function send(report: Report): Promise<boolean> {
  try {
    const res = await fetch("/api/me/client-errors", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(report),
      keepalive: true,
      signal: AbortSignal.timeout(10_000),
    });
    // A 4xx (customer, view-as, signed out) will never pass; drop it.
    return res.status < 500;
  } catch {
    return false;
  }
}

async function flush() {
  if (flushing) return;
  flushing = true;
  try {
    while (pending.length > 0) {
      if (!(await send(pending[0] as Report))) {
        setTimeout(() => void flush(), RETRY_MS);
        return;
      }
      pending.shift();
    }
  } finally {
    flushing = false;
  }
}

function report(r: Report) {
  if (pending.length >= MAX_PENDING) pending.shift();
  pending.push(r);
  void flush();
}

/** Logs an error screen to the server when it shows up and again, with its duration, when it goes. */
export function useReportErrorScreen(screen: Report["screen"], message: string, enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const path = window.location.pathname;
    const shownAt = Date.now();
    report({ screen, message, path, online: navigator.onLine });
    return () =>
      report({ screen, message, path, online: navigator.onLine, shownMs: Date.now() - shownAt });
  }, [screen, message, enabled]);
}
